import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Copy, Loader2, Mail, Sparkles } from "lucide-react";

/**
 * ScholarshipEmailDialog — AI-drafts a scholarship enquiry email for a
 * given (degree, scholarship). Opens `mailto:` or copies to clipboard.
 * Extracted from AdvancedResults.jsx (2026-02-28 refactor) so the
 * parent page stays under 1.2k lines.
 *
 * Props are unchanged.
 */
export default function ScholarshipEmailDialog({
  open,
  onOpenChange,
  degreeId,
  degreeName,
  universityName,
  scholarshipName,
}) {
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(null);
  const [body, setBody] = useState("");

  useEffect(() => {
    if (!open || !scholarshipName) return;
    let cancelled = false;
    setLoading(true);
    setDraft(null);
    setBody("");
    (async () => {
      try {
        const { data } = await api.post("/scholarships/draft-email", {
          degree_id: degreeId,
          scholarship_name: scholarshipName,
        });
        if (cancelled) return;
        setDraft(data);
        setBody(data.body || "");
      } catch (e) {
        if (cancelled) return;
        toast.error("Could not draft email. Try again in a moment.");
        onOpenChange(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, degreeId, scholarshipName, onOpenChange]);

  const copyAll = async () => {
    const full = `Subject: ${draft?.subject || ""}\n\n${body}`;
    try {
      await navigator.clipboard.writeText(full);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed — select the text manually.");
    }
  };

  const openMailto = () => {
    const s = encodeURIComponent(draft?.subject || "");
    const b = encodeURIComponent(body);
    window.location.href = `mailto:?subject=${s}&body=${b}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="scholarship-email-dialog">
        <DialogHeader>
          <DialogTitle className="font-editorial text-2xl">Draft scholarship email</DialogTitle>
          <DialogDescription className="text-xs">
            {scholarshipName} · {degreeName} · {universityName}
            {draft?.source === "template" && (
              <span className="ml-2 inline-flex items-center gap-1 text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5 text-[9px] uppercase tracking-wider">
                template
              </span>
            )}
            {draft?.source === "ai" && (
              <span className="ml-2 inline-flex items-center gap-1 text-violet-800 bg-violet-50 border border-violet-200 rounded-full px-1.5 py-0.5 text-[9px] uppercase tracking-wider">
                <Sparkles className="h-2.5 w-2.5" /> AI-drafted
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-16 flex items-center justify-center gap-3 text-stone-500 text-sm" data-testid="draft-loading">
            <Loader2 className="h-5 w-5 animate-spin" /> Drafting your first version…
          </div>
        ) : (
          <>
            <div>
              <div className="label-uppercase !text-[10px]">Subject</div>
              <div className="mt-1 text-sm font-mono border rounded-md px-3 py-2 bg-stone-50" data-testid="draft-subject">
                {draft?.subject}
              </div>
            </div>
            <div>
              <div className="label-uppercase !text-[10px]">Suggested recipient</div>
              <div className="mt-1 text-xs text-stone-600">{draft?.to_hint}</div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <div className="label-uppercase !text-[10px]">Email body — edit before sending</div>
                <div className="text-[10px] text-stone-400">{body.length} chars</div>
              </div>
              <Textarea
                data-testid="draft-body-textarea"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="mt-1 font-mono text-xs min-h-[280px]"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={copyAll} data-testid="draft-copy-btn">
                <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
              </Button>
              <Button size="sm" onClick={openMailto}
                      data-testid="draft-open-mailto-btn"
                      className="bg-[#4C1D95] hover:bg-[#3B0764] text-white">
                <Mail className="h-3.5 w-3.5 mr-1.5" /> Open in mail app
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
